type GtagCommand = [command: string, ...args: unknown[]];

type ClarityFunction = ((command: "event", name: AnalyticsEventName) => void) & {
  q?: IArguments[];
};

type AnalyticsWindow = Window & {
  dataLayer?: IArguments[];
  gtag?: (...args: GtagCommand) => void;
  clarity?: ClarityFunction;
};

export type AnalyticsEventName =
  | "cta_click"
  | "demo_open"
  | "contact_form_start"
  | "contact_form_success"
  | "contact_form_error"
  | "email_click";

type AnalyticsProperty = "label" | "location" | "destination" | "demo_name";
export type AnalyticsProperties = Partial<Record<AnalyticsProperty, string>>;

const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID?.trim();
const CLARITY_PROJECT_ID = import.meta.env.VITE_CLARITY_PROJECT_ID?.trim();

const EVENT_PROPERTIES: Record<AnalyticsEventName, readonly AnalyticsProperty[]> = {
  cta_click: ["label", "location", "destination"],
  demo_open: ["location", "demo_name"],
  contact_form_start: [],
  contact_form_success: [],
  contact_form_error: [],
  email_click: ["location", "destination"],
};

let initialized = false;
let lastPagePath: string | undefined;

function analyticsWindow() {
  return window as AnalyticsWindow;
}

function appendScript(id: string, source: string) {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = source;
  document.head.appendChild(script);
}

function initializeGa4() {
  if (!GA4_MEASUREMENT_ID) return;

  const target = analyticsWindow();
  target.dataLayer ??= [];
  target.gtag ??= function () {
    target.dataLayer?.push(arguments);
  };

  appendScript(
    "charehyar-ga4",
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_MEASUREMENT_ID)}`,
  );
  target.gtag("js", new Date());
  target.gtag("config", GA4_MEASUREMENT_ID, { send_page_view: false });
}

function initializeClarity() {
  if (!CLARITY_PROJECT_ID) return;

  const target = analyticsWindow();
  if (!target.clarity) {
    const clarity = function (this: unknown) {
      clarity.q ??= [];
      clarity.q.push(arguments);
    } as ClarityFunction;
    target.clarity = clarity;
  }

  appendScript(
    "charehyar-clarity",
    `https://www.clarity.ms/tag/${encodeURIComponent(CLARITY_PROJECT_ID)}`,
  );
}

export function initializeAnalytics() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  try {
    initializeGa4();
    initializeClarity();
  } catch {
    // Analytics must never affect the application experience.
  }
}

function safeProperties(name: AnalyticsEventName, properties: AnalyticsProperties) {
  const allowed = EVENT_PROPERTIES[name];
  return Object.fromEntries(
    allowed.flatMap((key) => {
      const value = properties[key]?.trim();
      return value ? [[key, value.slice(0, 100)]] : [];
    }),
  );
}

export function trackPageView(path: string, title: string) {
  if (!GA4_MEASUREMENT_ID || lastPagePath === path) return;
  lastPagePath = path;
  initializeAnalytics();

  try {
    analyticsWindow().gtag?.("event", "page_view", {
      page_location: new URL(path, window.location.origin).href,
      page_title: title,
    });
  } catch {
    // A blocked or unavailable analytics script is intentionally ignored.
  }
}

export function trackEvent(name: AnalyticsEventName, properties: AnalyticsProperties = {}) {
  if (!GA4_MEASUREMENT_ID && !CLARITY_PROJECT_ID) return;
  initializeAnalytics();

  try {
    if (GA4_MEASUREMENT_ID) {
      analyticsWindow().gtag?.("event", name, safeProperties(name, properties));
    }
  } catch {
    // A blocked or unavailable analytics script is intentionally ignored.
  }

  try {
    if (CLARITY_PROJECT_ID) analyticsWindow().clarity?.("event", name);
  } catch {
    // Clarity events are best-effort and must not affect the application.
  }
}
